import { useCallback, useEffect, useRef, useState } from 'react';

/*
 * Draft support for forms: keeps unsent form state in this browser (localStorage) so a
 * refresh or accidental navigation does not lose work.
 *
 *   const [form, setForm, draft] = useDraft('draft:new-custody', { name: '', amount: '' });
 *   ...
 *   <FormFooter status={draft.status}> ... </FormFooter>
 *   onSaved: draft.clear();
 *
 * Only for convenience state: never store passwords or card numbers in a draft.
 */
const PREFIX = 'yelo.draft.';

const read = (key) => {
    try {
        const raw = localStorage.getItem(PREFIX + key);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
};

export default function useDraft(key, initialValue, { delay = 500, enabled = true } = {}) {
    const stored = enabled && key ? read(key) : null;
    const [value, setValue] = useState(() => (stored && 'value' in stored ? stored.value : initialValue));
    const [savedAt, setSavedAt] = useState(stored ? stored.at : null);
    const [restored] = useState(Boolean(stored));
    const skipNext = useRef(true);
    const initialRef = useRef(initialValue);

    useEffect(() => {
        if (!enabled || !key) return undefined;
        if (skipNext.current) {
            skipNext.current = false;
            return undefined;
        }
        const t = setTimeout(() => {
            try {
                const at = Date.now();
                localStorage.setItem(PREFIX + key, JSON.stringify({ value, at }));
                setSavedAt(at);
            } catch (e) {
                // storage full or blocked: drafts are a convenience only
            }
        }, delay);
        return () => clearTimeout(t);
    }, [value, key, delay, enabled]);

    const clear = useCallback(() => {
        try { localStorage.removeItem(PREFIX + key); } catch (e) { /* ignore */ }
        skipNext.current = true;
        setSavedAt(null);
    }, [key]);

    const reset = useCallback(() => {
        clear();
        setValue(initialRef.current);
    }, [clear]);

    let status = null;
    if (savedAt) {
        const secs = Math.max(0, Math.round((Date.now() - savedAt) / 1000));
        status = secs < 60 ? 'Draft saved just now' : `Draft saved ${new Date(savedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    }

    return [value, setValue, { clear, reset, savedAt, restored, status }];
}
